import BaseClient from './baseClient';

type FindReqParams = {
  productCode?: string;
  styleCode?: string;
  productId?: string;
  nickname?: string;
  pageSize?: number;
  currentPage?: number;
  filterByNickname?: string;
};

type FindNicknameListParams = {
  indexKey?: string;
  nickname?: string;
  pageSize?: number;
  currentPage?: number;
};

export type NicknameProductSummary = {
  _id: string;
  productCode: string;
  styleCode: string;
  globalProductId: string;
  copy: { title: string; subtitle: string };
  squarishUrl: string;
  source: Record<string, string>;
};

export type NicknameGroup = {
  nickname: string;
  products: ProductItem[];
};

export type ProductItem = {
  _id: string;
  productCode: string;
  globalProductId: string;
  styleCode: string;
  productType: string;
  productSubType: string;
  nickName: string[];
  squarishUrl: string;
  copy: { title: string; subtitle: string };
  activations: {
    channelId: string;
    status: string;
    statusModifier: string;
    start: string;
    end: string;
    modificationDate: string;
  }[];
  source: Record<string, string>;
  customization?: { baseProductCode: string };
  marketplace: string;
  language: string;
  locationId: string;
  internalPid: string;
  updatedAt: Date;
  createdAt: Date;
};

export type NicknameOperationRequest = {
  nickname: string;
  productsToAdd?: string[];
  productsToRemove?: string[];
};

export type NicknameOperationResponse = {
  message: string;
  updateCount: number;
  errors: { productCode: string; message: string }[];
};

class NicknameClient extends BaseClient {
  async findProducts(params: FindReqParams): Promise<ProductItem[]> {
    const queryParams = new URLSearchParams();
    Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .forEach(([key, value]) => {
        queryParams.append(key, String(value));
      });
    const res: { productList: ProductItem[] } = await this.fetch(
      '/nickname/product/v2/list?' + queryParams,
    );
    return res.productList;
  }

  updateNicknames(productCode: string, nicknames: string[]) {
    return this.fetch(`/nickname/product/v2/id/${productCode}`, {
      method: 'PUT',
      body: JSON.stringify({ nickName: nicknames }),
    });
  }

  deleteNickname(productCode: string, nickname: string) {
    return this.fetch(`/nickname/product/v2/id/${productCode}`, {
      method: 'DELETE',
      body: JSON.stringify({ nickName: nickname }),
    });
  }

  nicknameOperation(
    body: NicknameOperationRequest,
  ): Promise<NicknameOperationResponse> {
    return this.fetch('/nickname/product/v2/nickname-operation', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async getNicknameList(
    params: FindNicknameListParams,
  ): Promise<{ groups: NicknameGroup[]; hasMore: boolean }> {
    const queryParams = new URLSearchParams();
    Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .forEach(([key, value]) => {
        queryParams.append(key, String(value));
      });
    const res: { objects: NicknameGroup[] } = await this.fetch(
      '/nickname/product/v2/nickname-list?' + queryParams,
    );
    const pageSize = params.pageSize ?? 0;
    return {
      groups: res.objects,
      hasMore: pageSize > 0 && res.objects.length === pageSize,
    };
  }
}

export default new NicknameClient();
